import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { ShieldCheck, Users, FolderCheck, Clock3 } from "lucide-react";

function AuthShell({ children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-10 bg-white">
        <Link to="/"><Logo /></Link>
        <div className="mt-12 max-w-sm w-full">{children}</div>
      </div>
      <div className="hidden lg:flex flex-col justify-center px-16 bg-gradient-to-br from-brand-navy to-brand-blue text-white relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        <h2 className="font-display font-extrabold text-4xl leading-tight relative">People. Records.<br />Compliance.<br />All in Flow.</h2>
        <p className="mt-5 text-brand-skysoft max-w-md relative">The HR platform built for Botswana SMEs to stay compliant with the Employment & Labour Relations Act, 2025.</p>
        <div className="mt-10 space-y-4 relative">
          {[{ i: Users, t: "Manage your whole team in one place" }, { i: FolderCheck, t: "Keep audit-ready statutory records" }, { i: ShieldCheck, t: "Compliance built into every step" }].map((f, idx) => (
            <div key={idx} className="flex items-center gap-3"><div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center"><f.i className="w-5 h-5" /></div><span className="text-brand-skysoft">{f.t}</span></div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.error) return setError(res.error);
    if (res.user?.role === "super_admin") return nav("/admin");
    nav("/app/dashboard");
  };

  return (
    <AuthShell>
      <h1 className="font-display font-bold text-2xl text-brand-navy">Welcome back</h1>
      <p className="text-slate-500 mt-1 mb-6">Sign in to your HRFlo account.</p>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid gap-2"><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.co.bw" required /></div>
        <div className="grid gap-2"><Label>Password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" required /></div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full bg-brand-blue hover:bg-brand-bluedark text-white rounded-lg h-11">{loading ? "Signing in..." : "Sign in"}</Button>
      </form>
      <p className="text-sm text-slate-600 mt-6">Don't have an account? <Link to="/signup" className="text-brand-blue font-semibold hover:underline">Create one</Link></p>
    </AuthShell>
  );
}

export function Signup() {
  const { signup } = useAuth();
  const [form, setForm] = useState({ orgName: "", name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const r = await signup(form);
    setLoading(false);
    if (r.error) return setError(r.error);
    if (r.pending) return setPending(r.message);
  };

  if (pending) {
    return (
      <AuthShell>
        <div className="w-11 h-11 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center mb-5"><Clock3 className="w-6 h-6" /></div>
        <h1 className="font-display font-bold text-2xl text-brand-navy">Account pending approval</h1>
        <p className="text-slate-600 mt-3">{pending}</p>
        <div className="mt-5 rounded-xl bg-brand-light/60 p-4 text-sm text-slate-600">
          Your <span className="font-semibold text-brand-navy">15-day demo</span> starts the moment HRFlo activates your organisation. We'll review your sign-up shortly.
        </div>
        <Link to="/login"><Button className="mt-6 w-full bg-brand-blue hover:bg-brand-bluedark text-white rounded-lg h-11">Back to sign in</Button></Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="font-display font-bold text-2xl text-brand-navy">Start your free demo</h1>
      <p className="text-slate-500 mt-1 mb-6">Create your organisation — approved accounts get a 15-day demo.</p>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid gap-2"><Label>Organisation name</Label><Input value={form.orgName} onChange={set("orgName")} placeholder="e.g. Kgalagadi Trading Co." required /></div>
        <div className="grid gap-2"><Label>Your full name</Label><Input value={form.name} onChange={set("name")} placeholder="e.g. Onkarabile Dintwe" required /></div>
        <div className="grid gap-2"><Label>Work email</Label><Input type="email" value={form.email} onChange={set("email")} placeholder="you@company.co.bw" required /></div>
        <div className="grid gap-2"><Label>Password</Label><Input type="password" value={form.password} onChange={set("password")} placeholder="Min. 8 characters" minLength={8} required /></div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full bg-brand-blue hover:bg-brand-bluedark text-white rounded-lg h-11">{loading ? "Creating..." : "Request free demo"}</Button>
      </form>
      <p className="text-sm text-slate-600 mt-6">Already have an account? <Link to="/login" className="text-brand-blue font-semibold hover:underline">Sign in</Link></p>
    </AuthShell>
  );
}
